import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  AlertTriangle, ArrowDown, ArrowUp, CheckCircle2, Clock, Coins, Gamepad2,
  Gift, ImageOff, Layers, Loader2, RefreshCw, RotateCcw, Save, Search, ServerCog,
  Sparkles, Ticket, X, Zap,
} from 'lucide-react';
import client from '../lib/api';
import MediaThumb from '../components/MediaThumb';
import { useToast } from '../hooks/use-toast';
import ConfirmDialog from './components/ConfirmDialog';
import { AdminError, AdminSkeleton } from './components/PageState';
// Hak listesinin sayfalaması sunucudadır (uç ``skip``/``limit`` alır ve
// ``total`` döndürür); boyut seçenekleri panelin diğer listeleriyle ortak.
import { PAGE_SIZE_OPTIONS, pageWindow, readPageSize, writePageSize }
  from './lib/pagination';
import './freespin.css';

/**
 * Freespin Kontrol — freespin'in tek yönetim ekranı.
 *
 * Freespin üç yerden verilir (bonus kampanyası, promosyon kodu, şans çarkı) ve
 * üçü de aynı sağlayıcı çağrısına iner. Buradaki değerler **varsayılandır**:
 * kampanya kendi değerini girmezse bunlar kullanılır, böylece hiçbir sayı koda
 * gömülü kalmaz. Sağlayıcıya ulaşılamadığı için kuyrukta kalan haklar da
 * buradan yeniden tanımlanır — oyuncunun ödülü sağlayıcı arızasıyla yanmaz.
 *
 * Ekran dört bölüme ayrılmıştır (varsayılanlar / oyun havuzu / verilen haklar /
 * sağlayıcılar): dördü tek sayfada alt alta dururken ayarların altındaki hak
 * listesi görünmüyordu ve yönetici kuyrukta bekleyen hakkı fark etmiyordu.
 * Sekme başlıkları sayıyı taşır, böylece kapalı bölüm de bilgi verir.
 *
 * **Kaydetme çubuğu sayfaya yapışıktır.** Varsayılanlar ile oyun havuzu tek
 * `PATCH /admin/settings` isteğine gider ama iki ayrı sekmede düzenlenir;
 * kaydet düğmesi yalnızca bir sekmenin içinde dursaydı havuzu değiştiren
 * yönetici değişikliğini kaydetmeden sayfadan çıkardı. Çubuk yalnızca
 * kaydedilmemiş değişiklik varken çizilir ve "geri al" ile son kayıtlı hâle
 * dönülür.
 *
 * Yerleşim ve renkler sayfaya özel `freespin.css` içindedir; `admin-*`
 * bileşenlerine dokunulmaz (Oyun Yönetimi sayfasındaki düzenin aynısı).
 */

const money = (value) => new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: 'TRY', maximumFractionDigits: 2,
}).format(Number(value || 0));

const count = (value) => new Intl.NumberFormat('tr-TR').format(Number(value || 0));

const formatDate = (value) => (value ? new Date(value).toLocaleString('tr-TR', {
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit',
}) : '—');

const formatDay = (value) => (value ? new Date(value).toLocaleDateString('tr-TR', {
  day: '2-digit', month: 'long', year: 'numeric',
}) : '—');

// Oyun listesi sayfa boyu — katalog binlerce satır olduğu için tek seferde
// çekilmez, "Daha fazla göster" ile büyür.
const GAME_PAGE = 48;

const RIGHT_STATUS = {
  completed: { label: 'Tanımlandı', tone: 'is-ok', icon: CheckCircle2 },
  awaiting_provider: { label: 'Kuyrukta', tone: 'is-warn', icon: Clock },
  pending: { label: 'Gönderiliyor', tone: 'is-muted', icon: Clock },
  cancelled: { label: 'İptal', tone: 'is-muted', icon: X },
};

const RIGHT_FILTERS = [
  { value: '', label: 'Tümü' },
  { value: 'awaiting_provider', label: 'Kuyrukta' },
  { value: 'completed', label: 'Tanımlandı' },
  { value: 'cancelled', label: 'İptal' },
];

const TABS = [
  { value: 'defaults', label: 'Varsayılanlar', icon: Zap },
  { value: 'pool', label: 'Oyun Havuzu', icon: Gamepad2 },
  { value: 'rights', label: 'Verilen Haklar', icon: Ticket },
  { value: 'sources', label: 'Sağlayıcılar', icon: ServerCog },
];

const number = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * Kaydedilecek gövdenin karşılaştırılabilir hâli.
 *
 * Kirlilik (dirty) bu anlık görüntüyle ölçülür: alanlar metin olarak tutulduğu
 * için "2" ile "2.0" ham karşılaştırmada farklı görünür ve çubuk hiç
 * kapanmazdı.
 */
const snapshot = (form, pool) => JSON.stringify({
  enabled: !!form.freespin_enabled,
  count: number(form.freespin_default_count),
  bet: number(form.freespin_default_bet),
  days: number(form.freespin_valid_days),
  win: number(form.freespin_win_multiplier),
  random: !!form.freespin_win_random,
  winMax: number(form.freespin_win_multiplier_max),
  wager: number(form.freespin_wager_multiplier),
  games: pool.map((game) => game.id),
});

const FreespinControl = () => {
  const [tab, setTab] = useState('defaults');
  const [overview, setOverview] = useState(null);
  const [form, setForm] = useState(null);
  // Son kayıtlı hâlin anlık görüntüsü — "geri al" ve kaydetme çubuğu buna bakar.
  const [baseline, setBaseline] = useState('');
  // Seçili oyun havuzu: [{ id, name, provider, api_provider_name, usable }]
  const [pool, setPool] = useState([]);

  const [games, setGames] = useState([]);
  const [gameTotal, setGameTotal] = useState(0);
  const [gameQuery, setGameQuery] = useState('');
  const [gamesBusy, setGamesBusy] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [rights, setRights] = useState([]);
  const [rightsTotal, setRightsTotal] = useState(0);
  const [rightsFilter, setRightsFilter] = useState('');
  const [rightsPage, setRightsPage] = useState(0);
  const [rightsSize, setRightsSize] = useState(
    () => readPageSize('freespin-rights'));
  const [rightsBusy, setRightsBusy] = useState(true);
  const [rightsNonce, setRightsNonce] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [confirm, setConfirm] = useState(null);
  const { toast } = useToast();

  const applyOverview = useCallback((data) => {
    setOverview(data);
    // Havuz sırası önceliktir: baştaki oyun kullanılamazsa sıradakine düşülür.
    const games_ = data.defaults.games || [];
    const next = {
      freespin_enabled: !!data.enabled,
      freespin_default_bet: data.defaults.bet || 0,
      freespin_default_count: data.defaults.count || 0,
      freespin_valid_days: data.defaults.valid_days || 7,
      freespin_win_multiplier: data.defaults.win_multiplier || 1,
      // İsteğe bağlı rastgele kazanç aralığı; kapalı kurulum eski davranıştadır.
      freespin_win_random: !!data.defaults.win_random_enabled,
      freespin_win_multiplier_max: data.defaults.win_multiplier_max || '',
      // Ayar kaydında -1 "ayarlanmamış" işaretidir ve servis onu sıfıra
      // çeker (``max(0, wager)``); panelde eksi bir çevrim çarpanı
      // göstermek yöneticiye anlamsız bir değer okuturdu.
      freespin_wager_multiplier: Math.max(0, number(data.defaults.wager_multiplier)),
    };
    setForm(next);
    setPool(games_);
    setBaseline(snapshot(next, games_));
  }, []);

  const loadOverview = useCallback(async () => {
    const { data } = await client.get('/admin/freespin/overview');
    applyOverview(data);
    return data;
  }, [applyOverview]);

  // Katalog binlerce satır; liste sayfa sayfa büyür. ``skip`` verildiğinde
  // eldekinin üstüne eklenir, verilmediğinde liste baştan kurulur.
  const loadGames = useCallback(async (term, skip = 0) => {
    const { data } = await client.get('/admin/freespin/games', {
      params: { q: term || '', skip, limit: GAME_PAGE },
    });
    const items = data.items || [];
    setGameTotal(number(data.total));
    setGames((prev) => (skip ? [...prev, ...items] : items));
  }, []);

  const loadMoreGames = async () => {
    setLoadingMore(true);
    try {
      await loadGames(gameQuery, games.length);
    } catch (e) {
      toast({
        title: 'Oyunlar alınamadı',
        description: e.response?.data?.detail || 'Tekrar deneyin',
      });
    } finally {
      setLoadingMore(false);
    }
  };

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await loadOverview();
    } catch (e) {
      setError(e.response?.data?.detail || 'Freespin bilgileri alınamadı.');
    } finally {
      setLoading(false);
    }
  }, [loadOverview]);

  useEffect(() => { reload(); }, [reload]);

  // Oyun araması yazarken beklesin; her tuşta istek atmasın. İlk yükleme
  // beklemez — açılışta boş bir katalog gösterip 350 ms sonra doldurmak
  // listeyi "yok" sanmaya yol açıyordu.
  const firstGameLoad = useRef(true);
  useEffect(() => {
    let alive = true;
    const run = async () => {
      setGamesBusy(true);
      try {
        await loadGames(gameQuery);
      } catch (e) {
        if (alive) {
          toast({
            title: 'Oyunlar alınamadı',
            description: e.response?.data?.detail || 'Tekrar deneyin',
          });
        }
      } finally {
        if (alive) setGamesBusy(false);
      }
    };
    if (firstGameLoad.current) {
      firstGameLoad.current = false;
      run();
      return () => { alive = false; };
    }
    const timer = setTimeout(run, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [gameQuery, loadGames, toast]);

  // Haklar sunucuda sayfalanır: süzgeç, sayfa ya da sayfa boyu değişince
  // yeniden okunur. ``rightsNonce`` yenile/tekrar dene sonrası artar.
  useEffect(() => {
    let alive = true;
    (async () => {
      setRightsBusy(true);
      try {
        const { data } = await client.get('/admin/freespin/rights', {
          params: {
            status: rightsFilter || '',
            skip: rightsPage * rightsSize,
            limit: rightsSize,
          },
        });
        if (!alive) return;
        setRights(data.items || []);
        setRightsTotal(number(data.total));
      } catch (e) {
        if (!alive) return;
        toast({
          title: 'Freespin hakları alınamadı',
          description: e.response?.data?.detail || 'Tekrar deneyin',
        });
      } finally {
        if (alive) setRightsBusy(false);
      }
    })();
    return () => { alive = false; };
  }, [rightsFilter, rightsPage, rightsSize, rightsNonce, toast]);

  const dirty = useMemo(
    () => !!form && baseline !== '' && snapshot(form, pool) !== baseline,
    [form, pool, baseline],
  );

  // Sekme kapatılırken uyarı: kaydetme çubuğu sayfanın içinde görünür, ama
  // sekmeyi kapatan yönetici onu hiç görmez.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const togglePoolGame = (game) => {
    setPool((prev) => (prev.some((g) => g.id === game.id)
      ? prev.filter((g) => g.id !== game.id)
      : [...prev, { ...game, usable: true }]));
  };

  // Sıra öncelik olduğu için havuz elle sıralanabilir olmalı.
  const movePoolGame = (index, delta) => {
    setPool((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const revert = () => {
    if (overview) applyOverview(overview);
  };

  const save = async () => {
    setSaving(true);
    try {
      const ids = pool.map((g) => g.id);
      await client.patch('/admin/settings', {
        freespin_enabled: !!form.freespin_enabled,
        // Tekil alan havuzun ilk üyesidir; eski kurulumlarla uyum için yazılır.
        freespin_default_game_ids: ids.join(','),
        freespin_default_game_id: ids[0] || '',
        freespin_default_bet: number(form.freespin_default_bet),
        freespin_default_count: number(form.freespin_default_count),
        freespin_valid_days: number(form.freespin_valid_days) || 7,
        freespin_win_multiplier: number(form.freespin_win_multiplier) || 1,
        freespin_wager_multiplier: Math.max(0, number(form.freespin_wager_multiplier)),
      });
      // Aralık `settings`'te değil ayrı kayıttadır; alt uç yukarıda yazıldığı
      // için doğrulama yeni değere göre yapılır.
      await client.put('/admin/freespin/win-range', {
        random_enabled: !!form.freespin_win_random,
        max_multiplier: Math.max(0, number(form.freespin_win_multiplier_max)),
      });
      toast({ title: 'Freespin ayarları kaydedildi' });
      await loadOverview();
    } catch (e) {
      toast({
        title: 'Kaydedilemedi',
        description: e.response?.data?.detail || 'Tekrar deneyin',
      });
    } finally {
      setSaving(false);
    }
  };

  const refreshRights = () => setRightsNonce((value) => value + 1);

  const retry = async (id) => {
    setBusyId(id);
    try {
      await client.post(`/admin/freespin/rights/${id}/retry`);
      toast({ title: 'Freespin sağlayıcıda tanımlandı' });
      refreshRights();
      await loadOverview();
    } catch (e) {
      toast({
        title: 'Tanımlanamadı',
        description: e.response?.data?.detail || 'Sağlayıcı yanıt vermedi',
      });
    } finally {
      setBusyId('');
    }
  };

  const cancelRight = async (id) => {
    setBusyId(id);
    try {
      await client.post(`/admin/freespin/rights/${id}/cancel`);
      toast({ title: 'Hak iptal edildi' });
      setConfirm(null);
      refreshRights();
      await loadOverview();
    } catch (e) {
      toast({
        title: 'İptal edilemedi',
        description: e.response?.data?.detail || 'Tekrar deneyin',
      });
    } finally {
      setBusyId('');
    }
  };

  // ---- türetilmiş değerler -------------------------------------------------

  const plan = useMemo(() => {
    if (!form) return null;
    const spins = Math.max(0, number(form.freespin_default_count));
    const bet = Math.max(0, number(form.freespin_default_bet));
    const win = number(form.freespin_win_multiplier) > 0
      ? number(form.freespin_win_multiplier) : 1;
    const days = Math.max(1, number(form.freespin_valid_days) || 7);
    const wager = Math.max(0, number(form.freespin_wager_multiplier));
    const winMax = number(form.freespin_win_multiplier_max);
    const random = !!form.freespin_win_random && winMax > win;
    const expires = new Date(Date.now() + days * 86400000);
    return {
      spins, bet, win, days, wager, expires, random, winMax,
      nominal: spins * bet,
      target: spins * bet * win,
      targetMax: spins * bet * (random ? winMax : win),
    };
  }, [form]);

  const usablePool = useMemo(
    () => pool.filter((game) => game.usable !== false).length,
    [pool],
  );

  const poolIds = useMemo(() => new Set(pool.map((game) => game.id)), [pool]);
  const pendingCount = number(overview?.pending_count);
  const capableSources = useMemo(
    () => (overview?.sources || []).filter((s) => s.supports_freespin !== false),
    [overview],
  );
  const readySources = capableSources.filter((s) => s.enabled).length;

  const pageCount = Math.max(1, Math.ceil(rightsTotal / rightsSize));
  // Sayfa sonundan bir kayıt silinirse (ya da süzgeç daraltılırsa) elde olmayan
  // bir sayfada kalınabiliyor; liste boş görünmesin diye geri çekilir.
  useEffect(() => {
    if (rightsPage > 0 && rightsPage >= pageCount) setRightsPage(pageCount - 1);
  }, [rightsPage, pageCount]);

  if (loading) {
    return (
      <div className="fs-page">
        <AdminSkeleton rows={6} label="Freespin ayarları yükleniyor" />
      </div>
    );
  }

  if (error || !form) {
    return (
      <div className="fs-page">
        <AdminError message={error} onRetry={reload} />
      </div>
    );
  }

  const systemOff = !form.freespin_enabled;

  return (
    <div className="fs-page">
      <header className="fs-head">
        <div className="fs-head-icon">
          <Sparkles size={21} aria-hidden="true" />
        </div>
        <div className="fs-head-text">
          <h1>Freespin Kontrol</h1>
          <p>
            Bonus kampanyası, promosyon kodu ve şans çarkı freespinlerinin ortak
            ayarları. Kampanya kendi değerini girmezse buradaki varsayılanlar
            kullanılır; sağlayıcıya ulaşılamadığı için kuyrukta kalan haklar da
            buradan yeniden tanımlanır.
          </p>
        </div>
        <div className="fs-head-actions">
          <span className={`fs-state-pill ${systemOff ? 'is-off' : 'is-on'}`}>
            {systemOff
              ? <X size={14} aria-hidden="true" />
              : <CheckCircle2 size={14} aria-hidden="true" />}
            {systemOff ? 'Freespin kapalı' : 'Freespin açık'}
          </span>
          <button type="button" className="admin-btn-secondary" onClick={reload}>
            <RefreshCw size={14} aria-hidden="true" /> Yenile
          </button>
        </div>
      </header>

      <div className="fs-stats">
        <article className={`fs-stat ${systemOff ? 'tone-bad is-toned' : 'tone-ok is-toned'}`}>
          <span className="fs-stat-label"><Zap size={12} aria-hidden="true" /> Sistem</span>
          <strong className="fs-stat-value">{systemOff ? 'Kapalı' : 'Açık'}</strong>
          <span className="fs-stat-hint">
            {systemOff
              ? 'Hiçbir kaynaktan freespin verilmez.'
              : 'Kampanya, promosyon kodu ve çark freespin verebilir.'}
          </span>
        </article>

        <article className="fs-stat tone-accent">
          <span className="fs-stat-label"><Gift size={12} aria-hidden="true" /> Varsayılan hak</span>
          <strong className="fs-stat-value">
            {count(plan.spins)} spin × {money(plan.bet)}
          </strong>
          <span className="fs-stat-hint">
            {plan.spins > 0 && plan.bet > 0
              ? `Nominal bahis ${money(plan.nominal)}`
              : 'Adet ya da bahis sıfır — kampanya kendi değerini vermeli.'}
          </span>
        </article>

        <article className="fs-stat tone-info">
          <span className="fs-stat-label"><Coins size={12} aria-hidden="true" /> Hedef kazanç</span>
          <strong className="fs-stat-value">{money(plan.target)}</strong>
          <span className="fs-stat-hint">
            Adet × bahis × {plan.win.toLocaleString('tr-TR')} çarpan
          </span>
        </article>

        <article className={`fs-stat ${usablePool ? 'tone-neutral' : 'tone-warn is-toned'}`}>
          <span className="fs-stat-label"><Layers size={12} aria-hidden="true" /> Oyun havuzu</span>
          <strong className="fs-stat-value">{count(pool.length)}</strong>
          <span className="fs-stat-hint">
            {pool.length
              ? `${count(usablePool)} tanesi kullanılabilir · ${count(gameTotal)} oyun seçilebilir`
              : 'Havuz boş — kampanya kendi oyununu seçmeli.'}
          </span>
        </article>

        <article className={`fs-stat ${pendingCount ? 'tone-warn is-toned' : 'tone-neutral'}`}>
          <span className="fs-stat-label"><Clock size={12} aria-hidden="true" /> Kuyrukta</span>
          <strong className="fs-stat-value">{count(pendingCount)}</strong>
          <span className="fs-stat-hint">
            {pendingCount
              ? 'Sağlayıcıda tanımlanamamış hak var; tekrar deneyin.'
              : 'Bekleyen hak yok.'}
          </span>
        </article>
      </div>

      {overview?.problem && (
        <div className="fs-note is-warn" role="status">
          <AlertTriangle size={15} aria-hidden="true" />
          <span>
            <b>Varsayılanlar eksik:</b> {overview.problem}. Kampanya kendi
            değerlerini girmezse freespin verilemez.
          </span>
        </div>
      )}

      <nav className="fs-tabs" aria-label="Freespin bölümleri">
        {TABS.map(({ value, label, icon: Icon }) => {
          const badge = value === 'pool'
            ? pool.length
            : (value === 'rights' ? rightsTotal : 0);
          return (
            <button
              key={value}
              type="button"
              className={`fs-tab ${tab === value ? 'is-active' : ''}`}
              aria-current={tab === value ? 'page' : undefined}
              onClick={() => setTab(value)}
            >
              <Icon size={14} aria-hidden="true" />
              {label}
              {value === 'rights' && pendingCount > 0 ? (
                <span className="fs-tab-count is-warn">{count(pendingCount)}</span>
              ) : (badge > 0 && <span className="fs-tab-count">{count(badge)}</span>)}
            </button>
          );
        })}
      </nav>

      {/* ---------------------------------------------------- varsayılanlar */}
      {tab === 'defaults' && (
        <section className="fs-panel">
          <div className="fs-panel-head">
            <Zap size={16} aria-hidden="true" />
            <div className="fs-panel-title">
              <h2>Varsayılan Ayarlar</h2>
              <p>
                Kampanya, promosyon kodu ya da çark dilimi kendi değerini
                girmezse buradaki değerler kullanılır. Hiçbir sayı koda gömülü
                değildir.
              </p>
            </div>
          </div>

          <div className="fs-panel-body">
            <label className={`fs-master ${form.freespin_enabled ? 'is-on' : ''}`}>
              <input
                type="checkbox"
                className="fs-switch-input"
                checked={!!form.freespin_enabled}
                onChange={(e) => setField('freespin_enabled', e.target.checked)}
              />
              <span className="fs-switch-track" aria-hidden="true"><i /></span>
              <span className="fs-switch-copy">
                <strong>Freespin sistemi açık</strong>
                <em>
                  Kapalıyken hiçbir kaynaktan freespin verilmez: bonus
                  kampanyası, promosyon kodu ve çark dilimi freespin ödülünü
                  tanımlayamaz.
                </em>
              </span>
            </label>

            <div className="fs-fields">
              <label className={`fs-field ${plan.spins <= 0 ? 'is-bad' : ''}`}>
                <span>Spin adedi</span>
                <span className="fs-field-wrap">
                  <input
                    type="number" min="0" className="admin-input"
                    value={form.freespin_default_count}
                    onChange={(e) => setField('freespin_default_count', e.target.value)}
                  />
                  <span className="fs-field-unit">spin</span>
                </span>
                <span className="fs-field-hint">
                  {plan.spins > 0
                    ? 'Kampanya adet vermezse bu kadar tur tanımlanır.'
                    : 'Sıfırken kampanya kendi adedini vermek zorundadır.'}
                </span>
              </label>

              <label className={`fs-field ${plan.bet <= 0 ? 'is-bad' : ''}`}>
                <span>Spin başına bahis</span>
                <span className="fs-field-wrap">
                  <input
                    type="number" min="0" step="0.01" className="admin-input"
                    value={form.freespin_default_bet}
                    onChange={(e) => setField('freespin_default_bet', e.target.value)}
                  />
                  <span className="fs-field-unit">₺</span>
                </span>
                <span className="fs-field-hint">
                  {plan.bet > 0
                    ? 'Oyuncunun bakiyesinden düşmez; sağlayıcıya bildirilen tur bahsidir.'
                    : 'Sıfır bahis sağlayıcıda hedef kazancı sıfırlar.'}
                </span>
              </label>

              <label className="fs-field">
                <span>Geçerlilik</span>
                <span className="fs-field-wrap">
                  <input
                    type="number" min="1" className="admin-input"
                    value={form.freespin_valid_days}
                    onChange={(e) => setField('freespin_valid_days', e.target.value)}
                  />
                  <span className="fs-field-unit">gün</span>
                </span>
                <span className="fs-field-hint">
                  Hakkın ömrü; kampanyanın bitiş tarihinden bağımsızdır.
                </span>
              </label>

              <label className="fs-field">
                <span>Kazanç çarpanı</span>
                <span className="fs-field-wrap">
                  <input
                    type="number" min="0.01" max="100" step="0.01" className="admin-input"
                    value={form.freespin_win_multiplier}
                    onChange={(e) => setField('freespin_win_multiplier', e.target.value)}
                  />
                  <span className="fs-field-unit">×</span>
                </span>
                <span className="fs-field-hint">
                  {form.freespin_win_random
                    ? 'Rastgele aralığın en düşük ucu.'
                    : 'Toplam hedef kazanç = adet × bahis × çarpan. Sıfır gönderilmez; boş bırakılırsa 1 kullanılır.'}
                </span>
              </label>

              <label className="fs-field">
                <span>Çevrim çarpanı</span>
                <span className="fs-field-wrap">
                  <input
                    type="number" min="0" step="0.1" className="admin-input"
                    value={form.freespin_wager_multiplier}
                    onChange={(e) => setField('freespin_wager_multiplier', e.target.value)}
                  />
                  <span className="fs-field-unit">×</span>
                </span>
                <span className="fs-field-hint">
                  {plan.wager > 0
                    ? `Kampanya kendi çevrimini girmezse x${plan.wager.toLocaleString('tr-TR')} uygulanır.`
                    : '0 geçerli bir değerdir: çevrimsiz.'}
                </span>
              </label>
            </div>

            {/* İsteğe bağlı: Gold Palace toplamı zorunlu ister ve tek çarpanla
                hakkı alan herkes aynı kazancı görür. Kapalıyken davranış aynıdır. */}
            <div className="fs-random">
              <label className={`fs-master ${form.freespin_win_random ? 'is-on' : ''}`}>
                <input
                  type="checkbox"
                  className="fs-switch-input"
                  checked={!!form.freespin_win_random}
                  onChange={(e) => setField('freespin_win_random', e.target.checked)}
                />
                <span className="fs-switch-track" aria-hidden="true"><i /></span>
                <span className="fs-switch-copy">
                  <strong>Rastgele kazanç (her üyeye farklı)</strong>
                  <em>
                    Kapalıyken hakkı alan herkes aynı toplamı kazanır. Açıkken
                    toplam her hak için kazanç çarpanı ile en yüksek çarpan
                    arasında ayrı ayrı seçilir. Yalnızca Gold Palace oyunlarını
                    etkiler.
                  </em>
                </span>
              </label>
              {form.freespin_win_random && (
                <div className="fs-fields">
                  <label className={`fs-field ${plan.random ? '' : 'is-bad'}`}>
                    <span>En yüksek kazanç çarpanı</span>
                    <span className="fs-field-wrap">
                      <input
                        type="number" min="0.01" max="100" step="0.01" className="admin-input"
                        value={form.freespin_win_multiplier_max}
                        onChange={(e) => setField('freespin_win_multiplier_max', e.target.value)}
                      />
                      <span className="fs-field-unit">×</span>
                    </span>
                    <span className="fs-field-hint">
                      {plan.random
                        ? 'Toplam = adet × bahis × (kazanç çarpanı ile bu değer arası rastgele).'
                        : 'Kazanç çarpanından büyük olmalıdır.'}
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* Yöneticinin asıl sorusu "bu ayarlarla oyuncu ne alacak" — rakamlar
                ayrı ayrı dururken bu ancak elde hesaplanabiliyordu. */}
            <div className="fs-preview">
              <div className="fs-preview-head">
                <Sparkles size={13} aria-hidden="true" /> Bu ayarlarla verilen hak
              </div>
              <dl className="fs-preview-rows">
                <div className="fs-preview-row">
                  <dt>Tur</dt>
                  <dd>{count(plan.spins)} spin</dd>
                  <small>her biri {money(plan.bet)} bahisle</small>
                </div>
                <div className="fs-preview-row">
                  <dt>Nominal bahis</dt>
                  <dd>{money(plan.nominal)}</dd>
                  <small>oyuncunun bakiyesinden düşmez</small>
                </div>
                <div className="fs-preview-row">
                  <dt>Hedef kazanç</dt>
                  <dd>
                    {plan.random
                      ? `${money(plan.target)} – ${money(plan.targetMax)}`
                      : money(plan.target)}
                  </dd>
                  <small>
                    {plan.random
                      ? 'her üyeye bu aralıkta rastgele'
                      : 'sağlayıcıya bildirilen toplam'}
                  </small>
                </div>
                <div className="fs-preview-row">
                  <dt>Son kullanma</dt>
                  <dd>{plan.days} gün</dd>
                  <small>bugün verilse {formatDay(plan.expires)}</small>
                </div>
                <div className="fs-preview-row">
                  <dt>Çevrim</dt>
                  <dd>
                    {plan.wager > 0
                      ? `x${plan.wager.toLocaleString('tr-TR')}`
                      : 'Çevrimsiz'}
                  </dd>
                  <small>
                    {plan.wager > 0 ? 'kampanya boş bırakırsa' : 'kazanç doğrudan yazılır'}
                  </small>
                </div>
                <div className="fs-preview-row">
                  <dt>Açılacak oyun</dt>
                  <dd>{pool.find((g) => g.usable !== false)?.name || '—'}</dd>
                  <small>
                    {pool.length > 1
                      ? `havuzdaki ${count(pool.length)} oyunun ilk kullanılabilir olanı`
                      : 'havuzun ilk sırası'}
                  </small>
                </div>
              </dl>
              <p className="fs-preview-foot">
                <b>Hak her zaman tek oyuna tanımlanır</b> — sağlayıcı çağrısı tek
                oyun alır. Havuzdaki alt sıralar yedektir: baştaki oyun
                pasifleşir ya da başka bir kayda birleştirilirse kampanya
                kopmadan sıradakine düşülür.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ------------------------------------------------------- oyun havuzu */}
      {tab === 'pool' && (
        <section className="fs-panel">
          <div className="fs-panel-head">
            <Gamepad2 size={16} aria-hidden="true" />
            <div className="fs-panel-title">
              <h2>Varsayılan Oyun Havuzu</h2>
              <p>
                Birden çok oyun seçebilirsiniz; <b>sıra önceliktir</b>. Listede
                yalnızca freespin verebilen sağlayıcıların aktif ve sitede
                görünen oyunları çıkar.
              </p>
            </div>
            {pool.length > 0 && (
              <div className="fs-panel-actions">
                <button type="button" className="admin-btn-secondary"
                        onClick={() => setPool([])}>
                  <X size={14} aria-hidden="true" /> Havuzu boşalt
                </button>
              </div>
            )}
          </div>

          <div className="fs-panel-body">
            <div className="fs-pool">
              <div className="fs-pool-col">
                <div className="fs-pool-caption">
                  <Layers size={13} aria-hidden="true" /> Seçili havuz
                  <span>{count(pool.length)} oyun</span>
                </div>

                {pool.length ? (
                  <div className="fs-pool-list">
                    {pool.map((game, index) => (
                      <div
                        key={game.id}
                        className={`fs-pool-item ${index === 0 ? 'is-primary' : ''} ${
                          game.usable === false ? 'is-broken' : ''}`}
                      >
                        <span className="fs-pool-rank">{index + 1}</span>
                        <div className="fs-pool-body">
                          <strong title={game.name}>{game.name}</strong>
                          <div className="fs-pool-meta">
                            {game.provider && <span>{game.provider}</span>}
                            {game.api_provider_name && (
                              <span className="fs-tag">{game.api_provider_name}</span>
                            )}
                            {index === 0 && game.usable !== false && (
                              <span className="fs-tag is-accent">önce bu denenir</span>
                            )}
                            {game.usable === false && (
                              <span className="fs-tag is-bad">kullanılamıyor</span>
                            )}
                          </div>
                        </div>
                        <div className="fs-pool-tools">
                          <button
                            type="button" className="fs-icon-btn" title="Yukarı taşı"
                            aria-label={`${game.name} sırasını yukarı taşı`}
                            disabled={index === 0}
                            onClick={() => movePoolGame(index, -1)}
                          >
                            <ArrowUp size={14} aria-hidden="true" />
                          </button>
                          <button
                            type="button" className="fs-icon-btn" title="Aşağı taşı"
                            aria-label={`${game.name} sırasını aşağı taşı`}
                            disabled={index === pool.length - 1}
                            onClick={() => movePoolGame(index, 1)}
                          >
                            <ArrowDown size={14} aria-hidden="true" />
                          </button>
                          <button
                            type="button" className="fs-icon-btn is-bad" title="Havuzdan çıkar"
                            aria-label={`${game.name} oyununu havuzdan çıkar`}
                            onClick={() => togglePoolGame(game)}
                          >
                            <X size={14} aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="fs-pool-empty">
                    <strong>Havuz boş</strong>
                    Kampanya kendi oyununu seçmezse freespin verilemez. Sağdaki
                    listeden en az bir oyun ekleyin.
                  </div>
                )}

                {pool.length > 0 && usablePool === 0 && (
                  <div className="fs-note is-bad">
                    <AlertTriangle size={15} aria-hidden="true" />
                    <span>
                      Havuzdaki oyunların <b>hiçbiri kullanılamıyor</b> (pasif ya
                      da başka bir kayda birleştirilmiş). Kullanılabilir bir oyun
                      ekleyin, yoksa hak tanımlanamaz.
                    </span>
                  </div>
                )}
              </div>

              <div className="fs-pool-col">
                <div className="fs-pool-caption">
                  <Gamepad2 size={13} aria-hidden="true" /> Oyun kataloğu
                  <span>{count(gameTotal)} oyun</span>
                </div>

                <div className="fs-search">
                  <Search size={15} aria-hidden="true" />
                  <input
                    className="admin-input"
                    placeholder="Oyun ya da üretici ara..."
                    value={gameQuery}
                    aria-label="Oyun ara"
                    onChange={(e) => setGameQuery(e.target.value)}
                  />
                  {gameQuery && (
                    <button
                      type="button" className="fs-search-clear"
                      aria-label="Aramayı temizle"
                      onClick={() => setGameQuery('')}
                    >
                      <X size={13} aria-hidden="true" />
                    </button>
                  )}
                </div>

                <div className="fs-catalog">
                  {games.map((game) => {
                    const picked = poolIds.has(game.id);
                    const art = game.image || '';
                    return (
                      <button
                        key={game.id}
                        type="button"
                        className={`fs-game ${picked ? 'is-picked' : ''}`}
                        aria-pressed={picked}
                        onClick={() => togglePoolGame(game)}
                      >
                        <span className="fs-game-art">
                          {art
                            ? <MediaThumb src={art} />
                            : <ImageOff size={18} aria-hidden="true" />}
                          {picked && (
                            <span className="fs-game-check">
                              <CheckCircle2 size={14} aria-hidden="true" />
                            </span>
                          )}
                        </span>
                        <span className="fs-game-body">
                          <strong title={game.name}>{game.name}</strong>
                          <span>{game.provider || game.api_provider_name}</span>
                        </span>
                      </button>
                    );
                  })}

                  {!games.length && (
                    <div className="fs-catalog-empty">
                      {gamesBusy ? (
                        <><Loader2 size={15} className="animate-spin" aria-hidden="true" /> Oyunlar yükleniyor…</>
                      ) : (gameQuery
                        ? `“${gameQuery}” aramasına uyan oyun yok.`
                        : 'Freespin tanımlanabilecek oyun bulunamadı. Sağlayıcı kapalı olabilir ya da oyunlar henüz senkronize edilmemiş olabilir.')}
                    </div>
                  )}

                  {games.length > 0 && games.length < gameTotal && (
                    <button
                      type="button" className="fs-more"
                      onClick={loadMoreGames} disabled={loadingMore}
                    >
                      {loadingMore && <Loader2 size={13} className="animate-spin" aria-hidden="true" />}
                      Daha fazla göster ({count(games.length)} / {count(gameTotal)})
                    </button>
                  )}
                </div>

                <p className="fs-catalog-foot">
                  {gameTotal
                    ? `Freespin tanımlanabilen ${count(gameTotal)} oyun var; ${count(games.length)} tanesi listeleniyor. Aramayla daraltabilirsiniz.`
                    : 'Freespin verebilen sağlayıcı açık değilse liste boş kalır — Sağlayıcılar sekmesine bakın.'}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ------------------------------------------------------ verilen haklar */}
      {tab === 'rights' && (
        <section className="fs-panel">
          <div className="fs-panel-head">
            <Ticket size={16} aria-hidden="true" />
            <div className="fs-panel-title">
              <h2>Verilen Freespin Hakları</h2>
              <p>
                Kaynağı fark etmeksizin bütün kampanya freespinleri. Sağlayıcıya
                ulaşılamadığı için kuyrukta kalan hak iptal edilmez; buradan
                yeniden denenir.
              </p>
            </div>
            <div className="fs-panel-actions">
              <button type="button" className="admin-btn-secondary" onClick={refreshRights}>
                <RefreshCw size={14} className={rightsBusy ? 'animate-spin' : ''} aria-hidden="true" />
                Yenile
              </button>
            </div>
          </div>

          <div className="fs-panel-body">
            <div className="fs-chips" role="group" aria-label="Durum süzgeci">
              {RIGHT_FILTERS.map(({ value, label }) => (
                <button
                  key={value || 'all'}
                  type="button"
                  className={`fs-chip ${rightsFilter === value ? 'is-active' : ''}`}
                  aria-pressed={rightsFilter === value}
                  onClick={() => { setRightsFilter(value); setRightsPage(0); }}
                >
                  {label}
                  {value === 'awaiting_provider' && pendingCount > 0 && (
                    <span className="fs-tab-count is-warn">{count(pendingCount)}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="fs-table-wrap">
            <table className="fs-table">
              <thead>
                <tr>
                  <th>Üye</th>
                  <th>Kampanya</th>
                  <th>Oyun</th>
                  <th className="fs-num">Spin</th>
                  <th className="fs-num">Bahis</th>
                  <th className="fs-num">Nominal</th>
                  <th>Durum</th>
                  <th>Tarih</th>
                  <th aria-label="İşlem" />
                </tr>
              </thead>
              <tbody>
                {rights.map((row) => {
                  const meta = RIGHT_STATUS[row.status] || RIGHT_STATUS.pending;
                  const StatusIcon = meta.icon;
                  const queued = row.status === 'awaiting_provider';
                  return (
                    <tr key={row.id}>
                      <td>
                        <span className="fs-user">
                          <strong>{row.username || '—'}</strong>
                          {row.source && <span>{row.source === 'bonus' ? 'Bonus kampanyası' : row.source}</span>}
                        </span>
                      </td>
                      <td>{row.title || '—'}</td>
                      <td>{row.game_name || '—'}</td>
                      <td className="fs-num">{count(row.count)}</td>
                      <td className="fs-num">{money(row.bet)}</td>
                      <td className="fs-num">{money(number(row.count) * number(row.bet))}</td>
                      <td>
                        <span className={`fs-status ${meta.tone}`}>
                          <StatusIcon size={12} aria-hidden="true" /> {meta.label}
                        </span>
                        {row.error && <div className="fs-row-error">{row.error}</div>}
                      </td>
                      <td>{formatDate(row.created_at)}</td>
                      <td>
                        {queued && (
                          <div className="fs-row-tools">
                            <button
                              type="button" className="admin-btn-secondary"
                              disabled={busyId === row.id}
                              onClick={() => retry(row.id)}
                            >
                              {busyId === row.id
                                ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                                : <RefreshCw size={12} aria-hidden="true" />}
                              Tekrar dene
                            </button>
                            <button
                              type="button" className="admin-btn-secondary is-bad"
                              disabled={busyId === row.id}
                              onClick={() => setConfirm(row)}
                            >
                              İptal
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {!rights.length && (
                  <tr>
                    <td colSpan={9} className="fs-table-empty">
                      <div>
                        {rightsBusy ? 'Haklar yükleniyor…' : (rightsFilter
                          ? 'Bu süzgece uyan hak yok.'
                          : 'Henüz freespin hakkı verilmemiş.')}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {rightsTotal > 0 && (
            <div className="fs-pager">
              <span className="fs-pager-info">
                {count(rightsTotal)} hak · sayfa {rightsPage + 1}/{pageCount}
              </span>
              <label className="fs-pager-size">
                Sayfa boyu
                <select
                  className="admin-input"
                  value={rightsSize}
                  onChange={(e) => {
                    const size = Number(e.target.value);
                    setRightsSize(size);
                    writePageSize('freespin-rights', size);
                    setRightsPage(0);
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="fs-pager-nav">
                <button
                  type="button" className="fs-pager-btn"
                  disabled={rightsPage === 0}
                  onClick={() => setRightsPage((value) => Math.max(0, value - 1))}
                >
                  Önceki
                </button>
                {pageWindow(rightsPage, pageCount).map((value, index) => (
                  value === 'gap' ? (
                    // eslint-disable-next-line react/no-array-index-key
                    <span key={`gap-${index}`} className="fs-pager-gap">…</span>
                  ) : (
                    <button
                      key={value}
                      type="button"
                      className={`fs-pager-btn ${value === rightsPage ? 'is-current' : ''}`}
                      onClick={() => setRightsPage(value)}
                    >
                      {value + 1}
                    </button>
                  )
                ))}
                <button
                  type="button" className="fs-pager-btn"
                  disabled={rightsPage >= pageCount - 1}
                  onClick={() => setRightsPage((value) => Math.min(pageCount - 1, value + 1))}
                >
                  Sonraki
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* -------------------------------------------------------- sağlayıcılar */}
      {tab === 'sources' && (
        <section className="fs-panel">
          <div className="fs-panel-head">
            <ServerCog size={16} aria-hidden="true" />
            <div className="fs-panel-title">
              <h2>Sağlayıcı Durumu</h2>
              <p>
                Hangi sağlayıcı oyuncu bazlı freespin verebiliyor. Freespin
                veremeyen sağlayıcılar da listelenir — oyun listesinde
                çıkmamalarının sebebi budur.
              </p>
            </div>
          </div>

          <div className="fs-panel-body">
            {readySources === 0 && (
              <div className="fs-note is-warn fs-note-gap">
                <AlertTriangle size={15} aria-hidden="true" />
                <span>
                  <b>Açık ve freespin verebilen sağlayıcı yok.</b> Ayarlar → Oyun
                  API sayfasından ilgili sağlayıcıyı açmadan hak tanımlanamaz.
                </span>
              </div>
            )}

            <div className="fs-sources">
              {(overview?.sources || []).map((source) => {
                const capable = source.supports_freespin !== false;
                return (
                  <article
                    key={source.source}
                    className={`fs-source ${capable ? '' : 'is-incapable'}`}
                  >
                    <div className="fs-source-head">
                      <strong>{source.name}</strong>
                      <span className={`fs-tag ${source.enabled ? 'is-accent' : ''}`}>
                        {source.enabled ? 'Açık' : 'Kapalı'}
                      </span>
                      {!capable && <span className="fs-tag">freespin vermez</span>}
                    </div>

                    {capable ? (
                      <>
                        <div className="fs-source-count">
                          {count(source.game_count)}
                          <span>oyunda freespin tanımlanabilir</span>
                        </div>
                        <p>
                          {source.enabled
                            ? 'Sağlayıcı açık; bu oyunlar havuz listesinde çıkar.'
                            : 'Sağlayıcı kapalı: oyunları havuz listesine girmez.'}
                        </p>
                      </>
                    ) : (
                      <p>
                        Oyuncu bazlı freespin vermiyor — oyunları havuz listesinde
                        çıkmaz.
                        {source.source === 'drakon' && (
                          <> Drakon freespin&apos;i kampanya modeliyle verir
                            (Pazarlama &amp; Bonus → Çapa Etkinliği).
                          </>
                        )}
                        {` Katalogda ${count(source.catalog_count)} aktif oyunu var.`}
                      </p>
                    )}
                  </article>
                );
              })}

              {!(overview?.sources || []).length && (
                <div className="fs-pool-empty">
                  <strong>Sağlayıcı bulunamadı</strong>
                  Oyun API ayarları tanımlanmadan freespin verilemez.
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {dirty && (
        <div className="fs-savebar" role="status">
          <div className="fs-savebar-text">
            <strong>Kaydedilmemiş değişiklik var</strong>
            <span>
              Varsayılanlar ve oyun havuzu birlikte kaydedilir; kaydedilmeden
              hiçbir kampanya bu değerleri kullanmaz.
            </span>
          </div>
          <div className="fs-savebar-tools">
            <button
              type="button" className="admin-btn-secondary"
              onClick={revert} disabled={saving}
            >
              <RotateCcw size={14} aria-hidden="true" /> Geri al
            </button>
            <button
              type="button" className="admin-btn-primary"
              onClick={save} disabled={saving}
            >
              {saving
                ? <Loader2 size={15} className="animate-spin" aria-hidden="true" />
                : <Save size={15} aria-hidden="true" />}
              Değişiklikleri kaydet
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        title="Freespin hakkı iptal edilsin mi?"
        description={confirm
          ? `${confirm.username || 'Üye'} için kuyrukta bekleyen ${confirm.count} spinlik hak kapatılacak. Sağlayıcıda tanımlanmış haklar bundan etkilenmez.`
          : ''}
        confirmLabel="Hakkı iptal et"
        busy={!!confirm && busyId === confirm.id}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm && cancelRight(confirm.id)}
      />
    </div>
  );
};

export default FreespinControl;
